import { is } from '../is.js';
import {
  AUTO_CLOSE,
  BUTTON,
  CHARTS,
  CLOSE_BUTTON,
  FORM,
  ICON,
  IMAGE,
  MINI_PLAYER,
  TEXT,
} from '../constants.js';
import { rewirePollOption } from './rewire-poll-option.js';
import scopeLogger from '../../../lib/scope-logger.js';

const console = scopeLogger('RMI:rewire creative');

/**
 * @param {RawCreativeSchema} schema
 * @return {*}
 */
export function rewireSchema(schema) {
  /*
  due to MiniPlayer widget ATM exist as single, shared widget it should
   for every such widget create own clone for every slide where it used.
   */
  for (const widgetId in schema.widgets) {
    const widget = schema.widgets[widgetId];
    widget.dataset ??= [];
    if (widget.type === MINI_PLAYER) {
      schema.slides.forEach((slide) => {
        const widgetIndex = slide.widgets.findIndex(
          (widget) => widget.id === widgetId,
        );
        if (widgetIndex === -1) {
          return;
        }
        const clone = JSON.parse(JSON.stringify(widget));
        const cloneId = `${widgetId}-${slide.id}`;
        clone.id = cloneId;
        schema.widgets[cloneId] = clone;
        slide.widgets[widgetIndex].id = cloneId;
      });
      delete schema.widgets[widgetId];
    }
  }

  /*
    Modify chart widgets, ATM it should be gauge chart and could be used only with quiz results
   */
  schema.slides.forEach((slide, _index) => {
    slide.dataset ??= [];
    slide.widgets.forEach((widget) => {
      if (widget.type === 'chart') {
        // create new widget, it should provide quiz results to chart
        const quizResultsId = `quizResults_${widget.id}`;
        schema.widgets[quizResultsId] = {
          id: quizResultsId,
          type: 'quizResultChart',
          _type: 'quizResultChart',
          widgets: [{ ...widget }],
          /* necessary while for recalculate size and position */
          get width() {
            return this.parent.width;
          },
          get height() {
            return this.parent.height;
          },
        };

        // modify widget to be a gauge chart
        schema.widgets[widget.id].type = CHARTS.GAUGE;
        // instruction for quiz result provider, what exactly need to provide into **this** chart
        schema.widgets[widget.id].propertyMapping = [
          ['correctnessRate', 'value'],
        ];

        widget.id = quizResultsId;
        widget.type = 'quizResultChart';
      }
    });
  });

  /* re-link widgets into tree */
  for (const widgetId in schema.widgets) {
    relinkWidgetIntoChildren(
      schema.widgets[widgetId],
      (widgetId) => schema.widgets[widgetId],
    );
  }

  /*  iterate widgets "list" and create slide accessor for every widget */
  for (const widgetKey in schema.widgets) {
    const widget = schema.widgets[widgetKey];
    // TODO move this patch to last stage, after all widgets created?
    Object.defineProperty(widget, 'slide', {
      get() {
        if (widget.parent === undefined) {
          console.warn(
            `attempt access to slide at widget without parent`,
            widget,
          );
          return undefined;
        }
        return widget.parent._type === 'slide'
          ? widget.parent
          : widget.parent.slide;
      },
    });
  }

  /*
  Handle "classes" definitions
 */
  schema.styleEntities ??= {};
  schema.classes = createClassesList(schema);
  /*
    merge classes, e.g. non-i18n, backgrounds into general collection
    for processing and using them in unified way
   */
  Object.assign(schema.backgrounds, schema.styleEntities.backgrounds);

  /*
  Iterate widgets by key and transform clickable widgets
  Almost for ET purposes
   */
  for (const widgetKey in schema.widgets) {
    const widget = schema.widgets[widgetKey];
    if (
      widget.type === BUTTON ||
      (widget.type === IMAGE && widget.isClickable)
    ) {
      let linkType = widget.linkType;
      /* patch `nextSlide` link to match unified pattern, see `useSlideNavigation` hook */
      const link =
        linkType === 'megogo://nextSlide' ? 'megogo://slide/next' : linkType;
      /* resolve link type according to their target */
      switch (true) {
        case link.startsWith('megogo://slide'):
        case link.startsWith('megogo://quiz_full_retry'): {
          linkType = 'creative_link';
          break;
        }
        case link.startsWith('megogo://'): {
          linkType = 'internal_link';
          break;
        }
        default: {
          linkType = 'external_link';
        }
      }
      widget.role = 'anchor';
      widget.link = link;
      widget.linkType = linkType;
      widget.dataset ??= [];
      if (widget.keys) {
        widget.dataset.push(['hotkey', widget.keys.join(',')]);
      }
    }
  }

  /*
   * For every slide:
   * - add slide type and self href
   * - add slide children
   * - add traversing to parent, next, prev slides
   * - transform close attr. into real button children
   */
  schema.slides.forEach((slide, index) => {
    slide.type = 'slide'; // for future mapping slide implementation via type
    slide._type = 'slide'; // this one required to properly find the nearest parent
    // add own href
    Object.assign(slide, { href: `megogo://slide/${index + 1}` });

    addSlideTraversingGetters(slide, index);

    /*
     map widgets into children tree
    */
    slide.children ??= [];

    // update quizResultQuestionsSet for backward compatibility
    if (slide.quizResultQuestionsSet && !Array.isArray(slide.quizResultQuestionsSet)) {
      slide.quizResultQuestionsSet = [slide.quizResultQuestionsSet];
    }

    // Create close button if necessary
    if (CLOSE_BUTTON in slide && slide.closeButton !== null) {
      // TODO: How we should place promo close button, inside container or slide
      slide.children.push(schema.widgets[slide.closeButton]);

      // patch promo close button to match normal button pattern
      Object.assign(schema.widgets[slide.closeButton], {
        type: BUTTON,
        role: CLOSE_BUTTON,
        disabled: !schema.widgets[slide.closeButton].linkType,
        link: schema.widgets[slide.closeButton].linkType || 'megogo://close',
        isFocusByDefault: false,
        isMainTarget: false,
        children: [],
        focusDirection: slide.closeButtonFocusDirections, // just keep
        version: schema.widgets[slide.closeButton].version,
      });

      Object.defineProperties(schema.widgets[slide.closeButton], {
        parent: {
          get() {
            return slide;
          },
        },
      });

      // apply auto close props to newly created button
      if (AUTO_CLOSE in slide && slide[AUTO_CLOSE] !== null) {
        const widget = schema.widgets[slide.closeButton];
        widget.autoClick = {
          timeout: slide[AUTO_CLOSE],
          appearance: {},
        };
        for (const state in widget.styledStates) {
          const stateStyles = widget.styledStates[state];
          if (AUTO_CLOSE in stateStyles) {
            widget.autoClick.appearance[state] = stateStyles[AUTO_CLOSE];
          }
        }
        Object.defineProperty(widget.autoClick.appearance, 'strokeWidth', {
          get() {
            return widget.autoClick.appearance.default.lineWidth;
          },
        });
      }

      // delete slide[AUTO_CLOSE]; // Do not delete!!! Required to fast check out of slot issue

      // sanitize close button
      delete schema.widgets[slide.closeButton].text;

      // sanitize slide
      delete slide[CLOSE_BUTTON];
      delete slide.closeButtonFocusDirections;
    }

    if (slide.form) {
      const form = {
        ...slide.form,
        type: FORM,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        borderRadius: 0,
      };

      Object.defineProperties(form, {
        parent: {
          get() {
            return slide;
          },
        },
        slide: {
          get() {
            return slide;
          }
        }
      });

      schema.widgets[form.id] = form;
      slide.children.push(form);
    }

    /*
     link all exists widgets into children tree
    */
    slide.widgets.forEach((widget) => {
      Object.defineProperties(schema.widgets[widget.id], {
        parent: {
          get() {
            return slide;
          },
        },
      });

      slide.children.push(schema.widgets[widget.id]);
    });

    delete slide.widgets;
  });

  /**
   * At this moment every widget should have parent,
   * otherwise remove orphan
   */
  for (const widgetId in schema.widgets) {
    const widget = schema.widgets[widgetId];
    if ('parent' in widget) {
      continue;
    }

    delete schema.widgets[widgetId];
    console.warn(`Orphan widget ${widgetId}, remove.`);
  }

  /*
   just for test in development mode, check slide reference for every widget
  */
  if (process.env.NODE_ENV === 'development') {
    for (const widgetKey in schema.widgets) {
      const widget = schema.widgets[widgetKey];
      if (!('slide' in widget)) {
        console.warn(`Widget ${widgetKey} does not have slide reference.`);
      }
    }
  }

  for (const widgetId in schema.widgets) {
    const widget = schema.widgets[widgetId];
    widget.children ??= [];
    widget.backgrounds ??= [];

    widget.addChild = function (child) {
      const parent = this;
      Object.defineProperties(child, {
        parent: {
          get() {
            return parent;
          },
        },
        slide: {
          get() {
            return parent.slide;
          },
        },
        key: {
          get() {
            return this.id;
          },
        },
      });
      schema.widgets[child.id] = child;
      this.children.push(child);
      console.log(`append ${child.id} to ${this.id} children list`);
    };

    if (is.widget.buttonType(widget)) {
      widget.link ??= widget.linkType;
      // Any button should not have border-radius depended on state, thoth here we level-up default state to basic property
      widget.borderRadius ??= widget.styledStates?.default?.borderRadius;

      // rewire custom props into children
      widget.children ??= [];
      if ('icon' in widget && widget.icon !== null) {
        const childrenId = `${widgetId}-icon`;
        schema.widgets[childrenId] = {
          id: childrenId,
          type: ICON,
          get key() {
            return childrenId;
          },
          src: widget.icon,
          /*
                    desc: {
                      id: childrenId,
                      src: widget.icon,
                    },
          */
          get parent() {
            return widget;
          },
          get slide() {
            return widget.slide;
          },
        };
        widget.children.push(schema.widgets[childrenId]);
        delete widget.icon;
      }
      if ('text' in widget && widget.text !== null) {
        const childrenId = `${widgetId}-text`;
        schema.widgets[childrenId] = {
          id: childrenId,
          type: TEXT,
          content: widget.text,
          get key() {
            return childrenId;
          },
          /*
                    desc: {
                      id: childrenId,
                      content: widget.text,
                    },
          */
          /*
                    styledStates: {
                      default: {
                        fontSize: widget.styledStates.default.fontSize,
                        color: widget.styledStates.default.textColor,
                      },
                      click: {
                        fontSize: widget.styledStates.click.fontSize,
                        color: widget.styledStates.click.textColor,
                      },
                      hover: {
                        fontSize: widget.styledStates.hover.fontSize,
                        color: widget.styledStates.hover.textColor,
                      },
                    },
          */
          get parent() {
            return widget;
          },
          get slide() {
            return widget.slide;
          },
        };
        widget.children.push(schema.widgets[childrenId]);
        /*
          // TODO: rewire ET event to getting innerText and allow removing that prop
          // DO NOT delete `text` property from button, used in ET
          delete widget.text;
        */
      }
    }

    if (is.widget.imageType(widget)) {
      widget.url = widget.url.replace(/^https?:/, '');
      widget.link ??= widget.linkType;
      widget.backgrounds = widget.overlays;

      delete widget.overlays;
    }

    /* TODO: find a way to move that transformation into polls extension */
    if (is.widget.pollOption(widget) || is.widget.quizOption(widget)) {
      const style = schema.styleEntities.styles[widget.styleId];
      widget.className = widget.styleId;
      widget.optionId ??= `fake-option-id_${widget.id}`;
      widget.sideEffects ??= [];
      widget.styledStates = style.styledStates;

      rewirePollOption(widget, schema.styleEntities);

      try {
        if (widget.slide?.pollState === 'vote' || widget.quizState === 'vote') {
          widget.role = widget.type;
          widget.type = BUTTON;
        } else {
          widget.role = widget.type;
          widget.type = 'div';
          widget.focusable = true;
        }
      } catch (error) {
        console.warn('Replacing poll option type failure', error);
      }

      // sanitize
      delete widget.styleId;
      delete widget.text;
      delete widget.imageUrl;
      delete widget._isModified;

      // TODO: replace size calculation directly on element props, not on w/h in class!!!

      // delete widget.height;
      // delete widget.width;
    }

    if (is.widget.scrollContainer(widget)) {
      widget.dataset.push(
        ['role', 'scroll-container'],
        ['scrollDirection', 'horizontal'], // for the moment the on one type of scroll is supported
      );
    }

    if (is.widget.numberPicker(widget)) {
      widget.minValue = widget.range[0];
      widget.maxValue = widget.range[1];

      const incBtn = {
        id: `${widget.id}_inc`,
        type: BUTTON,
        _type: 'INCREMENT',
        isFocusByDefault: widget.isFocusByDefault
          ? widget.initialValue < widget.maxValue
          : false,
      };

      const value = {
        id: `${widget.id}_input`,
        type: 'INPUT',
        _type: 'INPUT',
      };

      const decBtn = {
        id: `${widget.id}_dec`,
        type: BUTTON,
        _type: 'DECREMENT',
        isFocusByDefault: widget.isFocusByDefault
          ? widget.initialValue === widget.maxValue
          : false,
      };

      widget.addChild(decBtn);
      widget.addChild(value);
      widget.addChild(incBtn);
    }

    if (is.widget.slider(widget)) {
      widget.minValue = widget.range[0];
      widget.maxValue = widget.range[1];
    }
  }

  schema.styleEntities = undefined;
  return schema;

  /**
   * Append getters to access to parent and siblings from slide level.
   */
  function addSlideTraversingGetters(slide, index) {
    Object.defineProperties(slide, {
      parent: {
        get() {
          return schema;
        },
      },
      nextSibling: {
        get() {
          return schema.slides[index + 1];
        },
      },
      prevSibling: {
        get() {
          return schema.slides[index - 1];
        },
      },
      index: {
        get() {
          return index;
        },
      },
    });
  }
}

const remapVAlign = new Map([
  ['top', 'start'],
  ['middle', 'center'],
  ['bottom', 'end'],
]);

function createClassesList(schema) {
  const {
    styleEntities: {
      styles = {},
      widgets = {},
      //  TODO: processing background
      // backgrounds = {},
    } = {},
    originalSize,
  } = schema;

  const classes = [];

  for (const className in styles) {
    const {
      /* other classes reference or undefined */
      textWidget,
      imageWidget,
      percentTextWidget,
      resultProgressWidget,
      // consumers,
      ...props
    } = styles[className];

    // const widgetConsumer = schema.widgets.find((widget) => widget.className === className);

    const classDesc = {
      className,
      ...props,
      get parent() {
        /* very rude, just for fast recalculation sizes
         * TODO: workaround for alternative orientation */
        return originalSize;
      },
    };

    classes.push(classDesc);

    if (textWidget && textWidget in widgets) {
      const {
        /*
         // sanitize
        _isModified
         */

        /* consume */
        type,
        x,
        y,
        width,
        height,
        color,
        fontSize,
        lineHeight,
        hAlign,
        vAlign,
        isBold,
        isItalic,
        isUnderline,
        pollOptionResultStates,
        quizOptionResultStates,
      } = widgets[textWidget];

      classes.push({
        className: textWidget,
        type,
        x,
        y,
        width,
        height,
        color,
        fontSize,
        lineHeight,
        hAlign,
        vAlign: remapVAlign.get(vAlign),
        isBold,
        isItalic,
        isUnderline,
        pollOptionResultStates,
        quizOptionResultStates,
        get parent() {
          return classDesc;
        },
      });
    }

    if (imageWidget && imageWidget in widgets) {
      const {
        /*
         // sanitize
        url,
        linkType,
        isMainTarget,
        isClickable,
        _name,
        isFocusByDefault,
        _isModified,
          */

        /* consume */
        styledStates,
        pollOptionResultStates,
        quizOptionResultStates,
        type,
        x,
        y,
        width,
        height,
        fit,
        overlays,
      } = widgets[imageWidget];
      /*
if (imageWidget === "imageWidget_271393") {
  debugger;
}
*/
      classes.push({
        className: imageWidget,
        type,
        x,
        y,
        width,
        height,
        fit,
        overlays,
        styledStates,
        pollOptionResultStates,
        quizOptionResultStates,
        get parent() {
          return classDesc;
        },
      });
    }

    if (resultProgressWidget && resultProgressWidget in widgets) {
      const {
        type,
        backgrounds,
        borderRadius,
        height,
        width,
        x,
        y,
        progressBackgrounds,
        barDirection,
        barEdgeBorderRadius,
      } = widgets[resultProgressWidget];

      classes.push({
        className: resultProgressWidget,
        type,
        backgrounds,
        borderRadius,
        height,
        width,
        x,
        y,
        get progressBackgrounds() {
          console.warn(
            'Deprecated: use bar.backgrounds instead of progressBackgrounds',
          );
          return progressBackgrounds;
        },
        bar: {
          direction: barDirection,
          borderRadius: barEdgeBorderRadius,
          backgrounds: progressBackgrounds,
        },
        get parent() {
          return classDesc;
        },
      });
    }

    /*
        Almost the same as textWidget class, but with own className
     */
    if (percentTextWidget && percentTextWidget in widgets) {
      const {
        /*
                 // sanitize
                _isModified
                 */

        /* consume */
        type,
        x,
        y,
        width,
        height,
        color,
        fontSize,
        lineHeight,
        hAlign,
        vAlign,
        isBold,
        isItalic,
        isUnderline,
      } = widgets[percentTextWidget];

      classes.push({
        className: percentTextWidget,
        type,
        x,
        y,
        width,
        height,
        color,
        fontSize,
        lineHeight,
        hAlign,
        vAlign: remapVAlign.get(vAlign),
        isBold,
        isItalic,
        isUnderline,
        get parent() {
          return classDesc;
        },
      });
    }
  }

  schema.isPortrait = schema.originalSize.height > schema.originalSize.width;

  return classes;
}

function relinkWidgetIntoChildren(node, getWidget) {
  node.children ??= [];

  if (!node.widgets) {
    return;
  }

  node.widgets.forEach(({ id }) => {
    const widget = getWidget(id);
    Object.defineProperty(widget, 'parent', {
      configurable: true,
      get() {
        return node;
      },
    });
    node.children.push(widget);

    relinkWidgetIntoChildren(widget, getWidget);
  });
}

/**
 * @typedef {Object} RawSlideDescription
 * @property {string} id
 * @property {[{id: string, type: string}]} widgets
 * @property {[string]} backgrounds
 * @property rotatedBackgrounds
 * @property {*} closeButtonFocusDirections
 * @property {string|null} closeButton
 * @property {number} autoClose
 */

/**
 * @typedef {Object & RawSlideDescription} SlideDescription
 * @property {number} bulletIndex
 * @property {*} children
 * @property {SlideDescription} prevSibling
 * @property {SlideDescription} nextSibling
 * @property {[BackgroundDescription]} backgrounds
 * @property {number} index
 * @property {RawCreativeSchema} parent
 * @property {{name: string, timeout: {[key:(appear|enter|exit)]: number}}} transition
 */

/**
 * @typedef {Object} RawCreativeSchema
 * @property {{backgrounds: Object, styles: object, widgets: Object}} styleEntities
 * @property {{width: number, height: number}} originalSize
 * @property {[RawSlideDescription]} slides
 * @property {string|null} bulletsAlign
 * @property {boolean} isArrows
 * @property {boolean} isLoop
 * @property {boolean} isShowCloseButton TODO: clarify
 * @property {number} rotation
 * @property {boolean} isRotatedViewEnabled TODO: deprecated
 * @property {{[key:string]: RawWidgetDescription}} widgets
 * @property {{[key:string]: RawBackgroundDescription}} backgrounds
 * @property {*} slidesZeroPixel
 * @property {*} closeButton TODO: clarify against slides button
 */

/**
 * @typedef {{_isAutoHeight: boolean,
 *  _styleType: string,
 *  _isModified: boolean,
 *  _isAutoWidth: boolean,
 *  x: number,
 *  width: number,
 *  y: number,
 *  id: string,
 *  type: string,
 *  content: string,
 *  height: number}} RawWidgetDescription
 */
